import { logger } from './logger';
import { toast } from 'sonner';

/**
 * Checks if an error is a 503 Service Unavailable error
 */
interface ServiceError {
  status?: number;
  statusCode?: number;
  code?: string;
  message?: string;
  name?: string;
  response?: {
    status?: number;
    statusCode?: number;
  };
}

function isServiceUnavailable(error: unknown): boolean {
  if (!error) return false;
  const err = error as ServiceError;

  // Check for HTTP status 503 (various formats)
  if (err.status === 503 || err.code === '503' || err.statusCode === 503) return true;

  // Check error message (case insensitive)
  const message = String(err.message || '').toLowerCase();
  if (message.includes('503') ||
      message.includes('service unavailable') ||
      message.includes('service temporarily unavailable')) return true;

  // Check for Supabase-specific 503 indicators
  if (err.code === 'PGRST503' ||
      err.code === 'SERVICE_UNAVAILABLE' ||
      err.code === 'CIRCUIT_OPEN') return true;

  // Check for fetch/network errors that might indicate 503
  // Sometimes 503 errors come through as network errors
  if (err.name === 'HttpError' && err.status === 503) return true;

  // Check if the error has a response object with status 503
  if (err.response?.status === 503 || err.response?.statusCode === 503) return true;
  
  return false;
}

/**
 * Circuit breaker to prevent excessive requests when service is down
 */
class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private readonly threshold = 5; // Open circuit after 5 failures
  private readonly timeout = 60000; // 60 seconds before trying again
  private readonly halfOpenTimeout = 30000; // 30 seconds for half-open state

  isOpen(): boolean {
    if (this.failures < this.threshold) return false;
    
    const timeSinceLastFailure = Date.now() - this.lastFailureTime;
    
    // If enough time has passed, move to half-open state
    if (timeSinceLastFailure > this.timeout) {
      this.failures = Math.floor(this.threshold / 2); // Half-open state
      return false;
    }
    
    return true;
  }

  recordSuccess(): void {
    this.failures = 0;
    this.lastFailureTime = 0;
  }

  recordFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();
  }

  reset(): void {
    this.failures = 0;
    this.lastFailureTime = 0;
  }
}

// Global circuit breaker for Supabase requests
const supabaseCircuitBreaker = new CircuitBreaker();

/**
 * Retries a Supabase operation with exponential backoff
 * @param operation - Async function that returns Supabase query result
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param initialDelay - Initial delay in ms (default: 1000)
 * @returns The data if successful, null if error
 */
export async function retrySupabaseOperation<T>(
  operation: () => Promise<{ data: T | null; error: unknown }>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<{ data: T | null; error: unknown }> {
  // Check circuit breaker
  if (supabaseCircuitBreaker.isOpen()) {
    return {
      data: null,
      error: {
        message: 'Service temporarily unavailable. Please try again later.',
        code: 'CIRCUIT_OPEN',
        status: 503
      }
    };
  }

  let lastError: unknown = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      
      // If successful, record success and return
      if (!result.error) {
        supabaseCircuitBreaker.recordSuccess();
        return result;
      }
      
      // If it's a 503 error, retry with backoff
      if (isServiceUnavailable(result.error)) {
        lastError = result.error;
        
        if (attempt < maxRetries) {
          const delay = initialDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // Max retries reached
        supabaseCircuitBreaker.recordFailure();
        return result;
      }
      
      // For non-503 errors, don't retry
      supabaseCircuitBreaker.recordSuccess();
      return result;
      
    } catch (error) {
      lastError = error;
      
      // If it's a 503 error, retry with backoff
      if (isServiceUnavailable(error)) {
        if (attempt < maxRetries) {
          const delay = initialDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // Max retries reached
        supabaseCircuitBreaker.recordFailure();
        return { data: null, error };
      }
      
      // For non-503 errors, don't retry
      supabaseCircuitBreaker.recordSuccess();
      return { data: null, error };
    }
  }
  
  supabaseCircuitBreaker.recordFailure();
  return { data: null, error: lastError };
}

/**
 * Handles Supabase errors with consistent logging and user feedback
 * @param operation - Async function that returns Supabase query result
 * @param errorMessage - Message to log if error occurs
 * @param showToast - Whether to show toast notification (default: true)
 * @param retryOn503 - Whether to retry on 503 errors (default: true)
 * @returns The data if successful, null if error
 */
export async function handleSupabaseError<T>(
  operation: () => Promise<{ data: T | null; error: unknown }>,
  errorMessage: string,
  showToast = true,
  retryOn503 = true
): Promise<T | null> {
  try {
    const operationWithRetry = retryOn503 
      ? () => retrySupabaseOperation(operation)
      : operation;
    
    const { data, error } = await operationWithRetry();
    
    if (error) {
      // Don't show toast for 503 errors - they're service issues, not user errors
      const is503 = isServiceUnavailable(error);
      
      if (is503) {
        // Only log at debug level to avoid console spam
        logger.debug(errorMessage, { error, retried: retryOn503 });
      } else {
        logger.error(errorMessage, { error });
        if (showToast) {
          toast.error(getUserFriendlyMessage(error));
        }
      }
      
      return null;
    }
    
    return data;
  } catch (error) {
    const is503 = isServiceUnavailable(error);
    
    if (is503) {
      logger.debug(errorMessage, { error });
    } else {
      logger.error(errorMessage, { error });
      if (showToast) {
        toast.error(getUserFriendlyMessage(error));
      }
    }
    
    return null;
  }
}

/**
 * Gets user-friendly error message from error object
 * @param error - Error object
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (!error) {
    return "Something went wrong. Please try again.";
  }
  const err = error as ServiceError;

  // Network errors
  if (err?.message?.includes('network') ||
      err?.message?.includes('fetch') ||
      err?.code === 'NETWORK_ERROR' ||
      err?.message?.includes('Failed to fetch')) {
    return "Connection error. Please check your internet and try again.";
  }

  // Not found errors
  if (err?.code === 'PGRST116' ||
      err?.message?.includes('not found') ||
      err?.message?.includes('does not exist')) {
    return "The requested item was not found.";
  }

  // Permission errors
  if (err?.code === '42501' ||
      err?.message?.includes('permission') ||
      err?.message?.includes('RLS') ||
      err?.message?.includes('row-level security')) {
    return "You don't have permission to perform this action.";
  }

  // Authentication errors
  if (err?.code === 'PGRST301' ||
      err?.message?.includes('JWT') ||
      err?.message?.includes('authentication') ||
      err?.message?.includes('session')) {
    return "Your session has expired. Please refresh the page.";
  }

  // Validation errors
  if (err?.code === '23505' || // Unique constraint violation
      err?.message?.includes('duplicate') ||
      err?.message?.includes('already exists')) {
    return "This item already exists.";
  }

  // Foreign key constraint errors
  if (err?.code === '23503' ||
      err?.message?.includes('foreign key')) {
    return "Cannot perform this action due to related data.";
  }

  // Service unavailable errors (503)
  if (isServiceUnavailable(error)) {
    return "Service temporarily unavailable. Please try again in a moment.";
  }

  // Generic Supabase errors
  if (err?.message) {
    // Don't expose technical error messages in production
    if (import.meta.env.PROD) {
      return "Something went wrong. Please try again.";
    }
    // In development, show more details
    return err.message;
  }

  return "Something went wrong. Please try again.";
}

/**
 * Wraps an async operation with error handling
 * @param operation - Async function to execute
 * @param errorMessage - Message to log if error occurs
 * @param showToast - Whether to show toast notification (default: true)
 * @returns The result if successful, null if error
 */
export async function handleAsyncError<T>(
  operation: () => Promise<T>,
  errorMessage: string,
  showToast = true
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    logger.error(errorMessage, { error });
    if (showToast) {
      toast.error(getUserFriendlyMessage(error));
    }
    return null;
  }
}
